import React, { useEffect, useState } from "react";
import ChairGrid from "./ChairGrid";
import ListView from "./ListView";
import ChairRegistrationModal from "./ChairRegistrationModal";
import OccupiedSeatModal from "./OccupiedSeatModal";
import "../styles/ChairManager.css";
import { AllotSeatToStudent, getStudentDetails } from "../api/StudentDetails";
import { Occupant, Student, ViewMode } from "../types";
import { useEnvironment } from "../contexts/EnvironmentContext";

const ChairManager: React.FC = () => {
  const { config, switchEnvironment, isAdmin, canSwitchEnvironment } =
    useEnvironment();
  const [chairCount, setChairCount] = useState<number>(36);
  const [selectedChairs, setSelectedChairs] = useState<number[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("grid"); // 'grid' or 'list'
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedChairForRegistration, setSelectedChairForRegistration] =
    useState<number | null>(null);
  const [occupiedModalOpen, setOccupiedModalOpen] = useState<boolean>(false);
  const [selectedOccupiedChair, setSelectedOccupiedChair] = useState<
    number | null
  >(null);
  // Sample occupant data
  const [occupants, setOccupants] = useState<{ [key: number]: Occupant }>({
    1: {
      name: "John Smith",
      role: "Student",
      email: "john.smith@university.edu",
      status: "occupied",
      rfid: "RF001234",
      mobileNumber: "",
      studentType: "registered",
    },
    3: {
      name: "Sarah Johnson",
      role: "Test User",
      email: "sarah.johnson@test.com",
      status: "occupied",
      rfid: "",
      mobileNumber: "+1-555-0123",
      studentType: "test",
    },
    7: {
      name: "Mike Chen",
      role: "Student",
      email: "mike.chen@university.edu",
      status: "occupied",
      rfid: "RF009012",
      mobileNumber: "",
      studentType: "registered",
    },
    12: {
      name: "Emily Davis",
      role: "Student",
      email: "emily.davis@university.edu",
      status: "occupied",
      rfid: "RF003456",
      mobileNumber: "",
      studentType: "registered",
    },
    15: {
      name: "Alex Rodriguez",
      role: "Test User",
      email: "alex.rodriguez@test.com",
      status: "occupied",
      rfid: "",
      mobileNumber: "+1-555-0456",
      studentType: "test",
    },
    22: {
      name: "Lisa Wang",
      role: "Student",
      email: "lisa.wang@university.edu",
      status: "occupied",
      rfid: "RF002345",
      mobileNumber: "",
      studentType: "registered",
    },
    28: {
      name: "David Brown",
      role: "Student",
      email: "david.brown@university.edu",
      status: "occupied",
      rfid: "RF006789",
      mobileNumber: "",
      studentType: "registered",
    },
  });

  const generateChairs = (count: number): number[] => {
    return Array.from({ length: count }, (_, index) => index + 1);
  };

  const handleChairClick = (chairNumber: number): void => {
    const occ = occupants[chairNumber];
    if (occ && occ.status === "occupied") {
      setSelectedOccupiedChair(chairNumber);
      setOccupiedModalOpen(true);
      return;
    }
    // Only allow registration in admin mode for vacant seats
    if (config.allowRegistration) {
      setSelectedChairForRegistration(chairNumber);
      setModalOpen(true);
    }
  };

  const increaseChairs = (): void => {
    setChairCount((prev) => prev + 1);
  };

  const decreaseChairs = (): void => {
    if (chairCount > 1) {
      setChairCount((prev) => prev - 1);
      // Remove any selected chairs that are beyond the new count
      setSelectedChairs((prev) => prev.filter((chair) => chair <= prev.length));
    }
  };

  const clearSelection = (): void => {
    setSelectedChairs([]);
  };

  const toggleViewMode = (): void => {
    setViewMode((prev) => (prev === "grid" ? "list" : "grid"));
  };

  const handleModalClose = (): void => {
    setModalOpen(false);
    setSelectedChairForRegistration(null);
  };

  const handleChairRegistration = (
    chairNumber: number,
    occupantData: Occupant
  ): void => {
    const alloteStudent: Student = {
      seatAllotmentId: chairNumber,
      firstName: occupantData.name.split(" ")[0],
      lastName: occupantData.name.split(" ")[1] || "",
      emailId: occupantData.email,
      mobileNumber: occupantData.mobileNumber,
      rfIdentificationCode: occupantData.rfid,
      seatNumber: chairNumber.toString(),
    };
    debugger;
    AllotSeatToStudent(alloteStudent || ({} as Student))
      .then(() => {
        setOccupants((prev) => ({
          ...prev,
          [chairNumber]: {
            ...occupantData,
            status: "occupied",
            occupiedSince: new Date().toISOString(),
          },
        }));
        setModalOpen(false);
        setSelectedChairForRegistration(null);
      })
      .catch((error) => {
        console.error("Error allotting seat to student:", error);
      });
  };

  const chairs = generateChairs(chairCount);

  useEffect(() => {
    getStudentDetails()
      .then((data) => {
        // Transform data into occupants format if necessary
        const transformedOccupants: { [key: number]: Occupant } = {};
        console.log("Fetched student details:", data.data);
        data.data.forEach((student) => {
          transformedOccupants[student.seatAllotmentId] = {
            name: student.firstName + " " + student.lastName,
            role: "Student",
            email: student.emailId,
            status: "occupied",
            rfid: student.rfIdentificationCode,
            mobileNumber: student.mobileNumber,
            studentType: "registered",
          };
        });
        console.log("Fetched student details:", transformedOccupants);
        setOccupants(transformedOccupants);
      })
      .catch((error) => {
        console.error("Error setting occupants:", error);
      });
  }, []);

  return (
    <div className="chair-manager">
      <div className="controls">
        <div className="header-section">
          {canSwitchEnvironment ? (
            <div className="environment-toggle">
              <button
                onClick={() => switchEnvironment("admin")}
                className={`env-btn ${config.mode === "admin" ? "active" : ""}`}
              >
                Admin Mode
              </button>
              <button
                onClick={() => switchEnvironment("student")}
                className={`env-btn ${
                  config.mode === "student" ? "active" : ""
                }`}
              >
                Student Mode
              </button>
            </div>
          ) : (
            <div className="environment-indicator">
              <span className={`env-badge ${config.mode}`}>
                {config.mode === "admin" ? "Admin Mode" : "Student Mode"}
              </span>
            </div>
          )}
        </div>
        <div className="control-panel">
          <div className="chair-count-controls">
            <button
              onClick={decreaseChairs}
              disabled={chairCount <= 1}
              className="control-btn decrease"
            >
              -
            </button>
            <span className="chair-count">Chairs: {chairCount}</span>
            <button onClick={increaseChairs} className="control-btn increase">
              +
            </button>
          </div>
          <div className="selection-controls">
            <span className="selected-count">
              Selected: {selectedChairs.length}
            </span>
            <button
              onClick={clearSelection}
              className="control-btn clear"
              disabled={selectedChairs.length === 0}
            >
              Clear Selection
            </button>
          </div>
          <div className="view-controls">
            <button
              onClick={toggleViewMode}
              className={`view-toggle-btn ${
                viewMode === "grid" ? "active" : ""
              }`}
            >
              <span className="view-icon">⊞</span>
              Grid View
            </button>
            <button
              onClick={toggleViewMode}
              className={`view-toggle-btn ${
                viewMode === "list" ? "active" : ""
              }`}
            >
              <span className="view-icon">☰</span>
              List View
            </button>
          </div>
        </div>
      </div>
      {viewMode === "grid" ? (
        <ChairGrid
          chairs={chairs}
          selectedChairs={selectedChairs}
          onChairClick={handleChairClick}
          occupants={occupants}
        />
      ) : (
        <ListView
          chairs={chairs}
          selectedChairs={selectedChairs}
          onChairClick={handleChairClick}
          occupants={occupants}
        />
      )}

      <ChairRegistrationModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        onRegister={handleChairRegistration}
        chairNumber={selectedChairForRegistration}
        currentOccupant={
          selectedChairForRegistration
            ? occupants[selectedChairForRegistration]
            : null
        }
      />
      <OccupiedSeatModal
        isOpen={occupiedModalOpen}
        onClose={() => setOccupiedModalOpen(false)}
        onVacate={() => {
          if (selectedOccupiedChair !== null) {
            setOccupants((prev) => {
              const updated = { ...prev };
              delete updated[selectedOccupiedChair];
              return updated;
            });
            setOccupiedModalOpen(false);
            setSelectedOccupiedChair(null);
          }
        }}
        chairNumber={selectedOccupiedChair}
        occupant={
          selectedOccupiedChair ? occupants[selectedOccupiedChair] : null
        }
      />
    </div>
  );
};

export default ChairManager;
